// Utility helpers to detect YouTube and Spotify URLs and generate embed players.
// Shared between AdminMusic, DJDashboard and Wishes.

export const YT_PATTERNS = [
  /youtu\.be\/([\w-]{6,})/,
  /[?&]v=([\w-]{6,})/,
  /youtube\.com\/(?:embed|shorts)\/([\w-]{6,})/,
];

export const SP_RE = /open\.spotify\.com\/(?:intl-\w+\/)?(track|album|playlist|episode)\/([\w]+)/;

export const isYtUrl = (url) => {
  if (!url) return false;
  return YT_PATTERNS.some((p) => p.test(url)) || /[?&]list=([\w-]{6,})/.test(url);
};

export const ytId = (url) => {
  if (!url) return null;
  for (const p of YT_PATTERNS) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

export const isSpotifyUrl = (url) => !!url && SP_RE.test(url);

export const spotifyInfo = (url) => {
  const m = url?.match?.(SP_RE);
  return m ? { kind: m[1], id: m[2] } : null;
};

export const isMediaUrl = (url) => isYtUrl(url) || isSpotifyUrl(url);

/** Given a URL returns embed props (src, ratio) for an iframe, or null. */
export const embedFor = (url, opts = {}) => {
  const yt = ytId(url);
  if (yt) {
    const params = new URLSearchParams({ rel: "0", modestbranding: "1" });
    if (opts.autoplay) params.set("autoplay", "1");
    if (typeof window !== "undefined") {
      params.set("origin", window.location.origin);
    }
    // Use the privacy-enhanced (nocookie) domain — fewer embed restrictions
    return {
      provider: "youtube",
      src: `https://www.youtube-nocookie.com/embed/${yt}?${params.toString()}`,
      height: null,
      aspect: "video",
    };
  }
  const sp = spotifyInfo(url);
  if (sp) {
    return {
      provider: "spotify",
      src: `https://open.spotify.com/embed/${sp.kind}/${sp.id}?utm_source=hh`,
      height: sp.kind === "playlist" || sp.kind === "album" ? 352 : 152,
      aspect: null,
    };
  }
  return null;
};
